import { useContext, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  CalendarDays,
  ChevronDown,
  ChevronUp,
  CircleChevronRight,
  DiffIcon,
  MapPin,
  MinusCircle,
  PlusCircle,
} from 'lucide-react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { BASE_URL } from '../../routes/routeConstant';
import Breadcrumb from '../../components/Breadcrumb';
import useTourPackage from '../../stores/useTourPackage';
import NotFoundPage from '../../routes/NotFoundPage';
import Skeleton from '../../components/ui/skeleton';
import LayoutGuest from '../../components/layouts/LayoutGuest';
import { FormatPrice } from '../../utils/format';
import { Button } from '../../components/ui/button';
import { LanguageContext } from '../../contexts/LanguageContext';
import useCompanyProfile from '../../stores/useCompanyProfiles';

export default function DetailTour() {
  const { t } = useTranslation();
  const { language } =
    useContext(LanguageContext) ||
    (() => {
      throw new Error('LanguageSwitcher must be used within a LanguageProvider');
    })();
  const lang = language === 'id';
  const { fetchLoading, fetchTourById, tours, detailTour } = useTourPackage();
  const { profile } = useCompanyProfile();
  const [notFound, setNotFound] = useState(false);
  const [expandedItineraries, setExpandedItineraries] = useState<number[]>([]);
  const { id } = useParams();
  const [searchParams] = useSearchParams();

  const serviceId = searchParams.get('serviceId') || null;
  const tourId = Number(id);
  const filteredTour = tours.find((tour) => tour.tour_id === tourId);
  const dataTour = filteredTour || detailTour;
  const breadcrumbPath = [
    { name: t('navigation.home'), link: BASE_URL },
    ...(serviceId ? [{ name: t('general.tour'), link: `/service/${serviceId}` }] : []),
    { name: `${t('general.tour')} ${dataTour?.destination_tour || ''}` },
  ];

  useEffect(() => {
    if (tours.length === 0 && !fetchLoading && !dataTour) {
      fetchTourById(tourId);
    }
  }, [fetchTourById, tours, tourId, fetchLoading, dataTour]);

  useEffect(() => {
    if (!fetchLoading && dataTour === undefined) {
      setNotFound(true);
    }
  }, [fetchLoading, dataTour]);

  if (notFound) return <NotFoundPage />;

  const imageSlides = dataTour?.image_tour.map((url, i) => (
    <SwiperSlide key={url} className="md:w-96 overflow-hidden rounded-lg w-full">
      <div
        className="md:h-96 h-64 md:w-96 w-full bg-center rounded-lg bg-cover"
        style={{ backgroundImage: `url(${url})` }}
      >
        <div className="h-full bg-gradient-to-t flex justify-center items-end p-8 from-primary via-transparent to-transparent w-full">
          <p key={`${url}-title`}>{dataTour.image_titles[i]}</p>
        </div>
      </div>
    </SwiperSlide>
  ));

  const imageUrl = dataTour?.image_tour || [];

  const toggleItinerary = (day: number) => {
    setExpandedItineraries((prev) => (prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]));
  };

  const phoneNumber = profile?.whatsapp_pr;
  const message = lang
    ? `Halo, saya tertarik untuk memesan paket tour ke ${dataTour?.destination_tour}. Mohon informasi lebih lanjut mengenai harga dan jadwal. Terima kasih!`
    : `Hello, I am interested in booking a tour package to ${
        dataTour?.destination_tour_en ? dataTour?.destination_tour_en : dataTour?.destination_tour
      }. Could you please provide more details about the price and schedule? Thank you!`;
  const whatsappUrl = `https://wa.me/+62${phoneNumber}?text=${encodeURIComponent(message)}`;

  return (
    <LayoutGuest className="max-w-screen-xl px-2">
      {fetchLoading ? (
        <div className="flex flex-col gap-4">
          <Skeleton className="w-full h-full p-4 min-h-[60vh] flex flex-col justify-end gap-3">
            <Skeleton className="max-w-64 h-8 bg-neutral-200" />
            <Skeleton className="max-w-96 h-4 bg-neutral-200" />
          </Skeleton>
          <Skeleton className="max-w-64 h-8" />
          <Skeleton className="max-w-64 h-4 bg-neutral-200" />
          <Skeleton className="w-full h-64" />
          <Skeleton className="max-w-64 h-8" />
          <Skeleton className="max-w-64 h-4 bg-neutral-200" />
          <Skeleton className="w-full h-64" />
        </div>
      ) : (
        <>
          <header
            className="w-full bg-cover mt-[16px] bg-center rounded-lg overflow-hidden"
            style={{ backgroundImage: `url(${dataTour?.image_tour[0]})` }}
          >
            <div className="bg-gradient-to-r text-white rounded-lg sm:p-8 p-4 from-primary to-transparent h-full flex md:flex-row flex-col gap-4 md:justify-between justify-start w-full backdrop-blur-sm">
              <div className="flex flex-col gap-8 w-full whitespace-nowrap">
                <div className="overflow-x-scroll hide-scrollbar">
                  <Breadcrumb backStatus="hidden" paths={breadcrumbPath} className="text-white" />
                </div>
                <div className="flex flex-col md:justify-between h-full justify-start gap-8">
                  <div className="flex- flex-col gap-1">
                    <h1 className="sm:text-3xl text-2xl font-lato font-semibold">
                      {t('general.tour')}{' '}
                      {lang ? dataTour?.destination_tour : dataTour?.destination_tour_en || dataTour?.destination_tour}
                    </h1>
                    <h3 className="sm:text-xl text-base flex gap-2">
                      {t('general.duration')}: <p className="uppercase">{dataTour?.duration_tour}</p>
                    </h3>
                  </div>
                  <div className="p-2 bg-white/90 flex gap-4 w-fit rounded-lg overflow-hidden backdrop-blur-sm">
                    <div>
                      <p className="text-[8px] text-primary">{t('general.startfrom')}</p>
                      <p className="sm:text-xl text-base font-semibold text-secondary">
                        IDR {FormatPrice(dataTour?.price_tour)}
                      </p>
                    </div>
                    <Link to={whatsappUrl} target="_blank">
                      <Button type="button">{t('navigation.booking')}</Button>
                    </Link>
                  </div>
                </div>
              </div>
              <div>
                <Swiper
                  modules={[Pagination, Autoplay]}
                  autoplay={
                    imageUrl.length > 1 ? { delay: 2500, disableOnInteraction: false, pauseOnMouseEnter: true } : false
                  }
                  pagination={{ clickable: true, dynamicBullets: true, dynamicMainBullets: 1 }}
                  loop={imageUrl.length > 1}
                  centerInsufficientSlides
                  slidesPerView={1}
                  className="md:w-96 drop-shadow-xl rounded-lg w-full"
                >
                  {imageSlides}
                </Swiper>
              </div>
            </div>
          </header>
          <div className="px-2 mt-4 flex flex-col gap-6">
            <div className="">
              <div className="flex flex-col gap-1">
                <h3 className="flex gap-2 sm:text-xl text-base font-semibold font-lato">
                  <CalendarDays /> Itinerary
                </h3>
                <p className="text-secondNeutral sm:text-sm text-xs">{t('detail-tour.itinerary')}</p>
              </div>
              <div className="mt-4 flex flex-col gap-2">
                {dataTour?.itineraries.map((itn, index, array) => {
                  const isFirst = index === 0;
                  const isLast = index === array.length - 1;
                  const isExpanded = expandedItineraries.includes(itn.title_itn);

                  return (
                    <div key={itn.title_itn} className="border bg-neutral-50 border-border rounded-lg sm:p-4 p-3">
                      <button
                        type="button"
                        className="flex w-full justify-between items-center cursor-pointer"
                        onClick={() => toggleItinerary(itn.title_itn)}
                      >
                        <h4 className="flex gap-2 font-semibold sm:text-base text-sm items-center">
                          {isFirst || isLast ? (
                            <MapPin className="text-secondary sm:h-6 h-5" />
                          ) : (
                            <CircleChevronRight className="text-secondary sm:h-6 h-5" />
                          )}
                          {t('itinerary.title')} {itn.title_itn}
                        </h4>
                        {isExpanded ? (
                          <ChevronUp className="text-secondNeutral sm:h-6 h-5" />
                        ) : (
                          <ChevronDown className="text-secondNeutral sm:h-6 h-5" />
                        )}
                      </button>
                      {isExpanded && (
                        <div
                          className="mt-2 text-xs sm:text-sm prose prose-sm md:prose-base lg:prose-lg max-w-none"
                          dangerouslySetInnerHTML={{
                            __html: lang ? itn.activity_itn_id : itn.activity_itn_en || itn.activity_itn_id,
                          }}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="mb-8">
              <div className="flex flex-col gap-1">
                <h3 className="flex gap-2 sm:text-xl text-base font-semibold font-lato">
                  <DiffIcon /> Included & Excluded
                </h3>
                <p className="text-secondNeutral sm:text-sm text-xs">{t('detail-tour.include-exclude')}</p>
              </div>
              <div className="mt-4 flex flex-col gap-2">
                <div className="border bg-neutral-50 border-border transition-all duration-300 rounded-lg sm:p-4 p-3">
                  <h4 className="flex gap-2 font-semibold sm:text-base text-sm items-center">
                    <PlusCircle className="text-secondary sm:h-6 h-5" /> Included
                  </h4>
                  <div
                    className="mt-2 text-xs sm:text-sm prose prose-sm md:prose-base lg:prose-lg max-w-none"
                    dangerouslySetInnerHTML={
                      lang
                        ? { __html: dataTour?.inclusion_tour_id || '' }
                        : {
                            __html: dataTour?.inclusion_tour_en
                              ? dataTour?.inclusion_tour_en
                              : dataTour?.inclusion_tour_id || '',
                          }
                    }
                  />
                </div>
                <div className="border bg-neutral-50 border-border transition-all duration-300 rounded-lg sm:p-4 p-3">
                  <h4 className="flex gap-2 font-semibold sm:text-base text-sm items-center">
                    <MinusCircle className="text-secondary sm:h-6 h-5" /> Excluded
                  </h4>
                  <div
                    className="mt-2 text-xs w-full sm:text-sm prose prose-sm md:prose-base lg:prose-lg max-w-none"
                    dangerouslySetInnerHTML={
                      lang
                        ? { __html: dataTour?.exclusion_tour_id || '' }
                        : {
                            __html: dataTour?.exclusion_tour_en
                              ? dataTour?.exclusion_tour_en
                              : dataTour?.exclusion_tour_id || '',
                          }
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </LayoutGuest>
  );
}
